import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Blocks,
  Trash2,
  Settings2,
  Shield,
  Info,
  ExternalLink,
  Calendar,
  Tag,
  ArrowRight,
} from 'lucide-react';
import { appsApi } from '../../../api/endpoints/apps';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Toggle } from '../../../components/ui/Toggle';
import { Select } from '../../../components/ui/Select';
import { EmptyState } from '../../../components/ui/EmptyState';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useToast } from '../../../components/ui/Alert';
import { formatDate } from '../../../lib/formatters';
import { scopeLabel, categoryLabel } from '../../../lib/appScopes';
import { openAppManagePage } from '../../../lib/appManage';
import type { StoreApp, SettingsSchemaField } from '../../../types/app';

// Tracking pixels are managed in Settings → Tracking with a simpler UX,
// so we hide their app-store entries to avoid duplicate paths that do the same thing.
const HIDDEN_APP_IDS = new Set([
  'facebook-pixel',
  'tiktok-pixel',
  'snapchat-pixel',
  'google-analytics',
  'google-ads',
  'twitter-pixel',
]);

/** Only apps that declare settings (or already carry config) get a Settings
 *  button — the rest have nothing to show but an empty form. */
function hasSettings(app: StoreApp): boolean {
  if (app.settingsSchema && app.settingsSchema.length > 0) return true;
  return Object.keys(app.config ?? {}).length > 0;
}

interface Props {
  storeId: string;
}

export function AppsTab({ storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Details modal
  const [detailsApp, setDetailsApp] = useState<StoreApp | null>(null);

  // Settings modal
  const [configApp, setConfigApp] = useState<StoreApp | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, unknown>>({});

  // Uninstall confirm modal
  const [uninstallApp, setUninstallApp] = useState<StoreApp | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['apps', storeId],
    queryFn: () => appsApi.list(storeId),
    enabled: !!storeId,
  });

  const uninstallMutation = useMutation({
    mutationFn: (appId: string) => appsApi.uninstall(storeId, appId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps', storeId] });
      toast('success', t('apps.uninstallSuccess', 'App uninstalled successfully'));
      setUninstallApp(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const configMutation = useMutation({
    mutationFn: ({ appId, config }: { appId: string; config: Record<string, unknown> }) =>
      appsApi.updateConfig(storeId, appId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps', storeId] });
      toast('success', t('common.success'));
      setConfigApp(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  // -- Handlers --

  const openDetails = (app: StoreApp) => {
    setDetailsApp(app);
  };

  const openManage = async (app: StoreApp) => {
    try {
      const opened = await openAppManagePage(storeId, app.appId, app.manageUrl);
      if (!opened) toast('error', t('apps.popupBlocked', 'Allow pop-ups to open the app.'));
    } catch {
      toast('error', t('common.error'));
    }
  };

  const openConfig = (app: StoreApp) => {
    setConfigApp(app);
    if (app.settingsSchema && app.settingsSchema.length > 0) {
      const values: Record<string, unknown> = {};
      for (const field of app.settingsSchema) {
        const existing = app.config?.[field.key];
        values[field.key] = existing !== undefined ? existing : (field.defaultValue ?? '');
      }
      setConfigValues(values);
    } else {
      const cfg = (app.config ?? {}) as Record<string, string>;
      setConfigValues({ ...cfg });
    }
  };

  // -- Schema-driven settings field renderer --

  const renderSchemaField = (field: SettingsSchemaField) => {
    const value = configValues[field.key];
    const hint = field.description || field.hint;

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.key}>
            <Textarea
              label={field.label}
              value={(value as string) ?? ''}
              placeholder={field.placeholder}
              required={field.required}
              rows={4}
              onChange={(e) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.key}>
            <Input
              label={field.label}
              type="number"
              value={value !== undefined && value !== null ? String(value) : ''}
              placeholder={field.placeholder}
              required={field.required}
              onChange={(e) =>
                setConfigValues((prev) => ({
                  ...prev,
                  [field.key]: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );

      case 'checkbox':
      case 'boolean':
        return (
          <div key={field.key}>
            <Toggle
              label={field.label}
              description={hint}
              checked={Boolean(value)}
              onChange={(checked) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: checked }))
              }
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.key}>
            <Select
              label={field.label}
              value={(value as string) ?? ''}
              options={field.options ?? []}
              placeholder={field.placeholder}
              required={field.required}
              onChange={(e) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );

      case 'text':
      default:
        return (
          <div key={field.key}>
            <Input
              label={field.label}
              type="text"
              value={(value as string) ?? ''}
              placeholder={field.placeholder}
              required={field.required}
              onChange={(e) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );
    }
  };

  // -- Helper to render app logo --

  const renderAppLogo = (app: StoreApp, size: 'sm' | 'lg' = 'sm') => {
    const sizeClass = size === 'lg' ? 'h-16 w-16' : 'h-12 w-12';
    const iconSize = size === 'lg' ? 'h-8 w-8' : 'h-6 w-6';
    const imgSrc = app.icon || app.logoUrl;

    return (
      <div
        className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent`}
      >
        {imgSrc ? (
          <img src={imgSrc} alt="" className={`${iconSize} object-contain`} />
        ) : (
          <Blocks className={iconSize} />
        )}
      </div>
    );
  };

  const apps = (data?.apps ?? []).filter((a) => !HIDDEN_APP_IDS.has(a.appId));
  const installedApps = apps.filter((a) => a.isInstalled);

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Installed Apps Section */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">
            {t('apps.installedApps', 'Installed Apps')}
            {installedApps.length > 0 && (
              <span className="ml-2 text-sm font-normal text-text-muted">
                ({installedApps.length})
              </span>
            )}
          </h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/${storeId}/apps`)}
          >
            {t('apps.browseMarketplace', 'Browse Marketplace')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {installedApps.length === 0 ? (
          <EmptyState
            title={t('apps.noInstalled', 'No installed apps')}
            description={t(
              'apps.noInstalledDesc',
              'Browse the marketplace to discover and install apps for your store.',
            )}
          />
        ) : (
          <div className="space-y-3">
            {installedApps.map((app) => {
              const appScopes = app.scopes ?? app.permissions ?? [];
              return (
                <div
                  key={app._id}
                  className="rounded-lg border border-border-default bg-bg-tertiary p-4 transition-colors hover:border-accent/30"
                >
                  <div className="flex items-start gap-4">
                    {renderAppLogo(app)}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-text-primary">{app.name}</h3>
                        {app.category && (
                          <Badge variant="default">{categoryLabel(app.category, t)}</Badge>
                        )}
                        {app.isActive !== false && (
                          <Badge variant="success">{t('apps.active', 'Active')}</Badge>
                        )}
                      </div>
                      {app.description && (
                        <p className="text-sm text-text-secondary line-clamp-2 mb-2">
                          {app.description}
                        </p>
                      )}
                      {appScopes.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          <span className="inline-flex items-center gap-1 text-xs text-text-muted">
                            <Shield className="h-3 w-3" />
                          </span>
                          {appScopes.slice(0, 4).map((scope) => (
                            <span
                              key={scope}
                              title={scope}
                              className="inline-flex items-center rounded-md bg-bg-secondary px-2 py-0.5 text-xs text-text-muted"
                            >
                              {scopeLabel(scope, t)}
                            </span>
                          ))}
                          {appScopes.length > 4 && (
                            <span className="inline-flex items-center rounded-md bg-bg-secondary px-2 py-0.5 text-xs text-text-muted">
                              +{appScopes.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openDetails(app)}>
                          <Info className="h-3.5 w-3.5" />
                          {t('apps.details', 'Details')}
                        </Button>
                        {app.manageUrl && (
                          <Button variant="secondary" size="sm" onClick={() => openManage(app)}>
                            <ExternalLink className="h-3.5 w-3.5" />
                            {t('apps.manage', 'Manage')}
                          </Button>
                        )}
                        {hasSettings(app) && (
                          <Button variant="secondary" size="sm" onClick={() => openConfig(app)}>
                            <Settings2 className="h-3.5 w-3.5" />
                            {t('apps.settings', 'Settings')}
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setUninstallApp(app)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('apps.uninstall', 'Uninstall')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ====== Details Modal ====== */}
      <Modal
        isOpen={!!detailsApp}
        onClose={() => setDetailsApp(null)}
        title={detailsApp?.name ?? ''}
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setDetailsApp(null)}>
            {t('common.close', 'Close')}
          </Button>
        }
      >
        {detailsApp && (
          <div className="space-y-5">
            {/* Logo + basic info */}
            <div className="flex items-center gap-4">
              {renderAppLogo(detailsApp, 'lg')}
              <div className="min-w-0">
                <h3 className="text-lg font-semibold">{detailsApp.name}</h3>
                {detailsApp.author && (
                  <p className="text-sm text-text-muted">
                    {t('apps.by', 'by')} {detailsApp.author}
                  </p>
                )}
                {detailsApp.version && (
                  <p className="text-xs text-text-muted mt-0.5">v{detailsApp.version}</p>
                )}
              </div>
            </div>

            {/* Description */}
            {detailsApp.description && (
              <p className="text-sm text-text-secondary leading-relaxed">
                {detailsApp.description}
              </p>
            )}

            {/* Metadata */}
            <div className="grid gap-3 rounded-lg border border-border-default bg-bg-tertiary p-4 text-sm">
              {detailsApp.category && (
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-text-muted shrink-0" />
                  <span className="text-text-muted">{t('apps.category', 'Category')}:</span>
                  <span className="text-text-primary">
                    {categoryLabel(detailsApp.category, t)}
                  </span>
                </div>
              )}
              {detailsApp.installedAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-muted shrink-0" />
                  <span className="text-text-muted">
                    {t('apps.installedOn', 'Installed on')}:
                  </span>
                  <span className="text-text-primary">
                    {formatDate(detailsApp.installedAt)}
                  </span>
                </div>
              )}
              {detailsApp.website && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4 text-text-muted shrink-0" />
                  <span className="text-text-muted">{t('apps.website', 'Website')}:</span>
                  <a
                    href={detailsApp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline truncate"
                  >
                    {detailsApp.website}
                  </a>
                </div>
              )}
            </div>

            {/* Granted Scopes */}
            {(() => {
              const scopes = detailsApp.scopes ?? detailsApp.permissions ?? [];
              if (scopes.length === 0) return null;
              return (
                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium">
                    <Shield className="h-3.5 w-3.5 text-text-muted" />
                    {t('apps.grantedScopes', 'Granted Permissions')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {scopes.map((scope) => (
                      <Badge key={scope} variant="accent">
                        {scopeLabel(scope, t)}
                      </Badge>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-text-muted">
                    {t(
                      'apps.scopesReadOnly',
                      'Permissions are granted when you install the app. To change them, remove the app and install it again.',
                    )}
                  </p>
                </div>
              );
            })()}

            {/* Settings display (read-only) */}
            {detailsApp.settingsSchema &&
              detailsApp.settingsSchema.length > 0 &&
              detailsApp.config &&
              Object.keys(detailsApp.config).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('apps.currentSettings', 'Current Settings')}
                  </h4>
                  <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-2 text-sm">
                    {detailsApp.settingsSchema.map((field) => {
                      let displayValue = detailsApp.config?.[field.key];
                      if (displayValue === undefined || displayValue === '') {
                        displayValue = t('common.notSet', 'Not set');
                      } else if (field.type === 'boolean' || field.type === 'checkbox') {
                        displayValue = displayValue
                          ? t('common.enabled', 'Enabled')
                          : t('common.disabled', 'Disabled');
                      } else if (field.type === 'select') {
                        const opt = field.options?.find(
                          (o) => o.value === String(displayValue),
                        );
                        displayValue = opt ? opt.label : String(displayValue);
                      }
                      return (
                        <div key={field.key} className="flex justify-between">
                          <span className="text-text-muted">{field.label}</span>
                          <span className="text-text-primary font-medium">
                            {String(displayValue)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
        )}
      </Modal>

      {/* ====== Settings Modal ====== */}
      <Modal
        isOpen={!!configApp}
        onClose={() => setConfigApp(null)}
        title={`${t('apps.settings', 'Settings')} - ${configApp?.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfigApp(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() =>
                configApp &&
                configMutation.mutate({ appId: configApp.appId, config: configValues })
              }
              isLoading={configMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {configApp && (
          <div className="space-y-4">
            {configApp.settingsSchema && configApp.settingsSchema.length > 0
              ? configApp.settingsSchema.map((field) => renderSchemaField(field))
              : Object.entries(configValues).map(([key, value]) => (
                  <Input
                    key={key}
                    label={key}
                    value={value as string}
                    onChange={(e) =>
                      setConfigValues((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                  />
                ))}
          </div>
        )}
      </Modal>

      {/* ====== Uninstall Confirmation Modal ====== */}
      <Modal
        isOpen={!!uninstallApp}
        onClose={() => setUninstallApp(null)}
        title={t('apps.confirmUninstall', 'Confirm Uninstall')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setUninstallApp(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                uninstallApp && uninstallMutation.mutate(uninstallApp.appId)
              }
              isLoading={uninstallMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {t('apps.uninstall', 'Uninstall')}
            </Button>
          </>
        }
      >
        {uninstallApp && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {renderAppLogo(uninstallApp)}
              <div>
                <p className="font-medium">{uninstallApp.name}</p>
                {uninstallApp.author && (
                  <p className="text-xs text-text-muted">
                    {t('apps.by', 'by')} {uninstallApp.author}
                  </p>
                )}
              </div>
            </div>
            <p className="text-sm text-text-secondary">
              {t(
                'apps.uninstallWarning',
                'Are you sure you want to uninstall this app? All access tokens will be revoked and the app will lose access to your store data.',
              )}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
